import React, { useState } from "react"
import {
  Card,
  CardBody,
  Badge,
  Button,
  Nav,
  NavItem,
  NavLink,
  Collapse,
  Row,
  Col,
  Table,
} from "reactstrap"
import { GenericPagination } from "@/utils/GenericPagination"
import type { PropertyRow } from "@/views/pages/components/Categories/Properties/UpdatePropertiesContainer"
import type { RootState } from "@/services/store"

/** The current site's id as the store holds it (`s.sites.site._id`). */
type SiteId = NonNullable<RootState["sites"]["site"]>["_id"]

/** One field's change in a pending-change row's `diff` (Mixed on the server model). */
type FieldDiff = { from?: unknown; to?: unknown }

/** A `properties` diff value: the enabled property ids per country key. */
type PropertiesEntry = { key: string; value: unknown[] }

/** The mail a proposed rate came from (SCRUM-1868). */
type ChangeSource = {
  sender?: string
  subject?: string
  receivedAt?: string
  excerpt?: string
}

/**
 * A pending-change row (GET /api/pending-changes, models/pendingChange.js on
 * the server) as this view reads it. Everything beyond `_id` and `status` is
 * optional because the container hands its rows in typed as only those plus
 * `entityType`.
 */
export type PendingChangeItem = {
  _id: string
  status: string
  entityType?: string
  entityId?: string
  entityTitle?: string
  createdAt?: string
  submittedBy?: { name?: string; email?: string } | null
  diff?: Record<string, FieldDiff>
  changeset?: {
    name?: string
    target?: { propertyName?: string }
    source?: ChangeSource
  }
  source?: ChangeSource
}

export type DisplayPendingChangesViewProps = {
  pendingItems: PendingChangeItem[]
  reviewedItems: PendingChangeItem[]
  totalPending: number
  totalReviewed: number
  activeTab: string
  onTabChange: (tab: string) => void
  expandedId: string | null
  onToggleExpand: (id: string) => void
  onApprove: (id: string) => void
  onApproveChangeset?: (name: string) => void
  onReject: (id: string) => void
  pendingPage: number
  setPendingPage: (page: number) => void
  reviewedPage: number
  setReviewedPage: (page: number) => void
  PAGE_SIZE: number
  message: string
  allProperties: PropertyRow[]
  currentSiteId: SiteId | null
}

const TABS = [
  { key: "all", label: "All" },
  { key: "product", label: "Products" },
  { key: "category", label: "Categories" },
  { key: "subcategory", label: "Subcategories" },
]

const TYPE_COLORS = {
  product: "primary",
  category: "info",
  subcategory: "default",
}

const STATUS_COLORS = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
}

function isImageValue(val) {
  if (typeof val !== "string") return false
  return val.startsWith("http://") || val.startsWith("https://") || val.startsWith("data:image")
}

function renderLabelArray(arr) {
  if (!arr || arr.length === 0) return <span className="text-muted">—</span>
  return (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: "0.82rem" }}>
      {arr.map((item, i) => {
        const text = item?.value?.label || "—"
        const slug = item?.value?.slug
        // Static list (SCRUM-2184): a read-only snapshot of one pending change, never edited or reordered here.
        return <li key={i}>{text}{slug ? <span className="text-muted"> ({slug})</span> : null}</li>
      })}
    </ul>
  )
}

const CHECK = "✓"
const DASH = "—"

function PropToggle({ on }: { on: boolean }) {
  return on
    ? <span style={{ color: "#27ae60", fontWeight: 600 }}>{CHECK}</span>
    : <span style={{ color: "#adb5bd" }}>{DASH}</span>
}

function PropertiesDiffRow({ from, to, allProperties, currentSiteId }: {
  from: unknown
  to: unknown
  allProperties: PropertyRow[]
  currentSiteId: SiteId | null
}) {
  const [open, setOpen] = useState(false)

  const propMap: Record<string, PropertyRow> = {}
  allProperties.forEach((p) => { propMap[String(p._id)] = p })

  const fromEntries: PropertiesEntry[] = Array.isArray(from) ? from : []
  const toEntries: PropertiesEntry[] = Array.isArray(to) ? to : []
  const allKeys = [...new Set([...fromEntries.map((e) => e.key), ...toEntries.map((e) => e.key)])]

  // compute summary counts
  let totalAdded = 0, totalRemoved = 0
  allKeys.forEach((key) => {
    const beforeIds = new Set((fromEntries.find((e) => e.key === key)?.value || []).map(String))
    const afterIds = new Set((toEntries.find((e) => e.key === key)?.value || []).map(String))
    afterIds.forEach((id) => { if (!beforeIds.has(id)) totalAdded++ })
    beforeIds.forEach((id) => { if (!afterIds.has(id)) totalRemoved++ })
  })

  return (
    <tr>
      <td colSpan={3} style={{ padding: 0, borderTop: "1px solid #e9ecef" }}>
        <div
          style={{ padding: "8px 12px", cursor: "pointer", userSelect: "none", display: "flex", alignItems: "center", gap: 8 }}
          onClick={() => setOpen((o) => !o)}
        >
          <strong style={{ fontSize: "0.82rem" }}>properties</strong>
          {totalAdded > 0 && (
            <Badge color="success" pill style={{ fontSize: "0.7rem" }}>+{totalAdded} added</Badge>
          )}
          {totalRemoved > 0 && (
            <Badge color="danger" pill style={{ fontSize: "0.7rem" }}>-{totalRemoved} removed</Badge>
          )}
          <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#8898aa" }}>
            {open ? "▲ collapse" : "▼ expand"}
          </span>
        </div>

        {open && (
          <div style={{ padding: "0 12px 12px" }}>
            {allKeys.map((key) => {
              const beforeIds = new Set((fromEntries.find((e) => e.key === key)?.value || []).map(String))
              const afterIds = new Set((toEntries.find((e) => e.key === key)?.value || []).map(String))
              const allIds = [...new Set([...beforeIds, ...afterIds])]

              if (allIds.length === 0) return null

              return (
                <div key={key} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#8898aa", letterSpacing: "0.05em", marginBottom: 4 }}>
                    COUNTRY: {key}
                  </div>
                  <Table size="sm" style={{ margin: 0, tableLayout: "fixed", fontSize: "0.8rem" }}>
                    <colgroup>
                      <col style={{ width: "34%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "19%" }} />
                      <col style={{ width: "19%" }} />
                    </colgroup>
                    <thead>
                      <tr style={{ background: "#f6f9fc" }}>
                        <th style={{ fontSize: "0.72rem", color: "#8898aa", borderTop: "none", padding: "4px 6px" }}>PROPERTY</th>
                        <th style={{ fontSize: "0.72rem", color: "#8898aa", borderTop: "none", padding: "4px 6px", textAlign: "center" }}>STATUS</th>
                        <th style={{ fontSize: "0.72rem", color: "#8898aa", borderTop: "none", padding: "4px 6px", textAlign: "center" }}>ENABLED</th>
                        <th style={{ fontSize: "0.72rem", color: "#8898aa", borderTop: "none", padding: "4px 6px", textAlign: "center" }}>SHOW IN BOX</th>
                        <th style={{ fontSize: "0.72rem", color: "#8898aa", borderTop: "none", padding: "4px 6px", textAlign: "center" }}>IN LISTING</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allIds.map((id) => {
                        const prop = propMap[id]
                        const wasEnabled = beforeIds.has(id)
                        const isEnabled = afterIds.has(id)
                        const status = !wasEnabled && isEnabled ? "added"
                          : wasEnabled && !isEnabled ? "removed"
                          : "unchanged"
                        const box = prop?.box?.find?.((b) => b.key === currentSiteId)?.value ?? false
                        const show = prop?.show?.find?.((s) => s.key === currentSiteId)?.value ?? false

                        const rowBg = status === "added" ? "rgba(39,174,96,0.08)"
                          : status === "removed" ? "rgba(192,57,43,0.08)"
                          : "transparent"

                        return (
                          <tr key={id} style={{ background: rowBg }}>
                            <td style={{ padding: "4px 6px", fontWeight: status !== "unchanged" ? 600 : 400 }}>
                              {prop?.name || <span className="text-muted" style={{ fontSize: "0.72rem" }}>…{id.slice(-6)}</span>}
                            </td>
                            <td style={{ padding: "4px 6px", textAlign: "center" }}>
                              <Badge
                                color={status === "added" ? "success" : status === "removed" ? "danger" : "light"}
                                style={{ fontSize: "0.65rem" }}
                              >
                                {status}
                              </Badge>
                            </td>
                            <td style={{ padding: "4px 6px", textAlign: "center" }}>
                              <PropToggle on={isEnabled} />
                            </td>
                            <td style={{ padding: "4px 6px", textAlign: "center" }}>
                              <PropToggle on={box} />
                            </td>
                            <td style={{ padding: "4px 6px", textAlign: "center" }}>
                              <PropToggle on={show} />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              )
            })}
          </div>
        )}
      </td>
    </tr>
  )
}

function isLabelArray(val) {
  return Array.isArray(val) && (val.length === 0 || val[0]?.siteid !== undefined || val[0]?.value?.label !== undefined)
}

function isPropertiesArray(val) {
  return Array.isArray(val) && val.length > 0 && val[0]?.key !== undefined && Array.isArray(val[0]?.value)
}

function renderValue(val, allProperties: PropertyRow[] = []) {
  if (val === null || val === undefined) return <span className="text-muted">—</span>
  if (typeof val === "boolean") return <code>{String(val)}</code>
  if (typeof val === "string" && isImageValue(val)) {
    return (
      <img
        src={val}
        alt=""
        style={{ maxHeight: "64px", maxWidth: "120px", objectFit: "contain", borderRadius: "4px", display: "block" }}
      />
    )
  }
  if (isLabelArray(val)) return renderLabelArray(val)
  if (Array.isArray(val)) {
    if (val.length === 0) return <span className="text-muted">—</span>
    return <span style={{ fontSize: "0.82rem" }}>{val.length} item{val.length !== 1 ? "s" : ""}</span>
  }
  if (typeof val === "object" && val !== null) {
    const imgSrc = val.secure_url || val.data || val.url
    if (imgSrc && isImageValue(imgSrc)) {
      return (
        <img
          src={imgSrc}
          alt=""
          style={{ maxHeight: "64px", maxWidth: "120px", objectFit: "contain", borderRadius: "4px", display: "block" }}
        />
      )
    }
    return <code style={{ fontSize: "0.78rem", wordBreak: "break-all" }}>{JSON.stringify(val)}</code>
  }
  return <span>{String(val)}</span>
}

// SCRUM-1629: an empty list counts as nothing too. A queued page activation
// carries `redirect_from: { from: null, to: [] }` whenever the update screen
// posts its (empty) redirect list against a live page that never had one
// (measured on the compose stack, 2026-09-15), and that rendered as a row
// reading "— → —" on every reviewed page.
const isBlank = (val) =>
  val === null || val === undefined || (Array.isArray(val) && val.length === 0)

const isMeaninglessChange = (from, to) => isBlank(from) && isBlank(to)

// SCRUM-1628: a queued page activation carries one diff entry per changed
// content variable, keyed `page_content.<variable>` (utils/pageActivation.js
// on the server), so the reviewer reads the field that changed rather than two
// whole page_content arrays. The row label is the variable name; the values
// render like any other field (a string as text, a list as "N items").
const PAGE_CONTENT_PREFIX = "page_content."

function DiffSection({ diff, allProperties, currentSiteId }: {
  diff?: Record<string, FieldDiff>
  allProperties: PropertyRow[]
  currentSiteId: SiteId | null
}) {
  const entries = Object.entries(diff || {}).filter(
    ([, { from, to }]) => !isMeaninglessChange(from, to)
  )
  if (!entries.length) return <p className="text-muted small mb-0">No field-level changes recorded.</p>

  return (
    <Table size="sm" className="mb-0" style={{ tableLayout: "fixed" }}>
      <thead>
        <tr style={{ background: "#f6f9fc" }}>
          <th style={{ width: "20%", fontSize: "0.75rem", color: "#8898aa", borderTop: "none" }}>FIELD</th>
          <th style={{ width: "40%", fontSize: "0.75rem", color: "#8898aa", borderTop: "none" }}>BEFORE</th>
          <th style={{ width: "40%", fontSize: "0.75rem", color: "#8898aa", borderTop: "none" }}>AFTER</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([field, { from, to }]) => {
          if (field === "properties" && (isPropertiesArray(from) || isPropertiesArray(to))) {
            return (
              <PropertiesDiffRow
                key={field}
                from={from}
                to={to}
                allProperties={allProperties}
                currentSiteId={currentSiteId}
              />
            )
          }
          const contentVariable = field.startsWith(PAGE_CONTENT_PREFIX)
            ? field.slice(PAGE_CONTENT_PREFIX.length)
            : null
          return (
            <tr key={field}>
              <td style={{ borderTop: "1px solid #e9ecef" }}>
                {contentVariable !== null && (
                  <span className="text-muted" style={{ display: "block", fontSize: "0.7rem" }}>
                    page_content
                  </span>
                )}
                <strong style={{ fontSize: "0.82rem" }}>{contentVariable ?? field}</strong>
              </td>
              <td style={{ background: "#fff5f5", borderTop: "1px solid #e9ecef", color: "#c0392b" }}>
                {renderValue(from, allProperties)}
              </td>
              <td style={{ background: "#f0fff4", borderTop: "1px solid #e9ecef", color: "#27ae60" }}>
                {renderValue(to, allProperties)}
              </td>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}

// SCRUM-1868: a `productProperties` row (one property value from a changeset,
// SCRUM-1838) reads as one line — product · property · old → new — so the
// reviewer sees the rate without opening the diff. The property name comes
// from the changeset target, or from the `property:<name>` diff key
// (utils/changeset.js on the server) when the target lacks it.
const PROPERTY_FIELD_PREFIX = "property:"

function RateLine({ change }: { change: PendingChangeItem }) {
  const [field, entry] = Object.entries(change.diff || {})[0] || []
  if (!entry) return null
  const property =
    change.changeset?.target?.propertyName ||
    (field.startsWith(PROPERTY_FIELD_PREFIX) ? field.slice(PROPERTY_FIELD_PREFIX.length) : field)
  const show = (v) => (v === undefined || v === null || v === "" ? "—" : String(v))
  return (
    <div data-testid="rate-line" style={{ fontSize: "0.9rem", color: "#32325d" }}>
      {`${change.entityTitle || change.entityId} · ${property} · ${show(entry.from)} → ${show(entry.to)}`}
    </div>
  )
}

// SCRUM-1868: the mail a proposed rate came from, when the row carries one
// (`change.source`: sender, subject, receivedAt, excerpt). No source, no block.
function SourceBlock({ source }: { source?: ChangeSource }) {
  if (!source) return null
  const received = source.receivedAt
    ? new Date(source.receivedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : null
  return (
    <div
      role="region"
      aria-label="Source"
      style={{ marginTop: "8px", padding: "8px 12px", background: "#f6f9fc", borderRadius: "0.375rem", fontSize: "0.8rem" }}
    >
      <div style={{ fontWeight: 600, color: "#8898aa" }}>Source</div>
      <div>{source.sender}</div>
      <div style={{ fontWeight: 600 }}>{source.subject}</div>
      {received && <div style={{ color: "#8898aa" }}>{received}</div>}
      {source.excerpt && (
        <blockquote style={{ margin: "4px 0 0", paddingLeft: "8px", borderLeft: "3px solid #dee2e6" }}>
          {source.excerpt}
        </blockquote>
      )}
    </div>
  )
}

type ChangeCardProps = {
  change: PendingChangeItem
  expanded: boolean
  onToggleExpand: (id: string) => void
  onApprove: (id: string) => void
  onApproveChangeset?: (name: string) => void
  onReject: (id: string) => void
  isPending: boolean
  allProperties: PropertyRow[]
  currentSiteId: SiteId | null
}

function ChangeCard({ change, expanded, onToggleExpand, onApprove, onApproveChangeset, onReject, isPending, allProperties, currentSiteId }: ChangeCardProps) {
  // SCRUM-1838: a row queued from a changeset file can be approved with the
  // rest of its changeset in one go.
  const changesetName = change.changeset?.name
  const submittedBy = change.submittedBy
  const displayName = submittedBy ? (submittedBy.name || submittedBy.email || "Unknown") : "—"
  const date = new Date(change.createdAt as string).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  })
  const meaningfulDiffCount = Object.values(change.diff || {}).filter(
    ({ from, to }) => !isMeaninglessChange(from, to)
  ).length
  const hasDiff = meaningfulDiffCount > 0

  return (
    <Card className="shadow-sm mb-3" style={{ border: "1px solid #e9ecef", borderRadius: "0.5rem" }}>
      <CardBody className="py-3 px-4">
        <Row className="align-items-center">
          <Col xs="auto">
            <Badge
              color={TYPE_COLORS[change.entityType as string] || "secondary"}
              style={{ fontSize: "0.7rem", letterSpacing: "0.05em", padding: "4px 8px" }}
            >
              {change.entityType?.toUpperCase()}
            </Badge>
          </Col>
          <Col>
            <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#32325d" }}>
              {change.entityTitle || change.entityId}
            </div>
            <div style={{ fontSize: "0.8rem", color: "#8898aa" }}>
              by {displayName} · {date}
            </div>
          </Col>
          <Col xs="auto" className="d-flex align-items-center" style={{ gap: "8px" }}>
            <Badge color={STATUS_COLORS[change.status] || "secondary"}>
              {change.status}
            </Badge>
            {hasDiff && (
              <Button
                size="sm"
                color="secondary"
                outline
                onClick={() => onToggleExpand(change._id)}
                style={{ fontSize: "0.78rem" }}
              >
                {expanded ? "Hide diff" : `View diff (${meaningfulDiffCount})`}
              </Button>
            )}
            {isPending && (
              <>
                <Button size="sm" color="success" onClick={() => onApprove(change._id)}>
                  Approve
                </Button>
                {changesetName && onApproveChangeset && (
                  <Button
                    size="sm"
                    color="success"
                    outline
                    onClick={() => onApproveChangeset(changesetName)}
                  >
                    Approve changeset {changesetName}
                  </Button>
                )}
                <Button size="sm" color="danger" onClick={() => onReject(change._id)}>
                  Reject
                </Button>
              </>
            )}
          </Col>
        </Row>

        {change.entityType === "productProperties" && (
          <div style={{ marginTop: "8px" }}>
            <RateLine change={change} />
            {/* SCRUM-1867: the propose route keeps the mail in `changeset.source`. */}
            <SourceBlock source={change.source ?? change.changeset?.source} />
          </div>
        )}

        <Collapse isOpen={expanded}>
          <div style={{ marginTop: "16px", borderTop: "1px solid #e9ecef", paddingTop: "12px" }}>
            <DiffSection diff={change.diff} allProperties={allProperties} currentSiteId={currentSiteId} />
          </div>
        </Collapse>
      </CardBody>
    </Card>
  )
}

function SectionHeader({ title, count, colorClass = "warning" }: { title: string; count: number; colorClass?: string }) {
  return (
    <div className="d-flex align-items-center mb-3" style={{ gap: "10px" }}>
      <h3 className="mb-0" style={{ color: "#32325d", fontWeight: 700 }}>{title}</h3>
      {count > 0 && (
        <Badge color={colorClass} pill style={{ fontSize: "0.78rem" }}>{count}</Badge>
      )}
    </div>
  )
}

const DisplayPendingChangesView = ({
  pendingItems,
  reviewedItems,
  totalPending,
  totalReviewed,
  activeTab,
  onTabChange,
  expandedId,
  onToggleExpand,
  onApprove,
  onApproveChangeset,
  onReject,
  pendingPage,
  setPendingPage,
  reviewedPage,
  setReviewedPage,
  PAGE_SIZE,
  message,
  allProperties,
  currentSiteId,
}: DisplayPendingChangesViewProps) => {
  return (
    <div style={{ maxWidth: "960px", margin: "0 auto" }}>

      {message && (
        <div
          className="alert alert-success mb-4"
          style={{ borderRadius: "0.5rem", fontSize: "0.9rem" }}
        >
          {message}
        </div>
      )}

      <Nav tabs className="mb-4" style={{ borderBottom: "2px solid #e9ecef" }}>
        {TABS.map((tab) => (
          <NavItem key={tab.key}>
            <NavLink
              className={activeTab === tab.key ? "active" : ""}
              onClick={() => onTabChange(tab.key)}
              style={{
                cursor: "pointer",
                fontWeight: activeTab === tab.key ? 600 : 400,
                color: activeTab === tab.key ? "#5e72e4" : "#8898aa",
                borderBottom: activeTab === tab.key ? "2px solid #5e72e4" : "2px solid transparent",
                padding: "8px 16px",
              }}
            >
              {tab.label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <div className="mb-5">
        <SectionHeader title="Pending Review" count={totalPending} colorClass="warning" />
        {pendingItems.length === 0 ? (
          <Card className="shadow-sm" style={{ border: "1px solid #e9ecef", borderRadius: "0.5rem" }}>
            <CardBody className="text-center py-5 text-muted">
              No pending changes to review.
            </CardBody>
          </Card>
        ) : (
          <>
            {pendingItems.map((change) => (
              <ChangeCard
                key={change._id}
                change={change}
                expanded={expandedId === change._id}
                onToggleExpand={onToggleExpand}
                onApprove={onApprove}
                onApproveChangeset={onApproveChangeset}
                onReject={onReject}
                isPending
                allProperties={allProperties}
                currentSiteId={currentSiteId}
              />
            ))}
            <div className="d-flex justify-content-center mt-3">
              <GenericPagination
                totalPages={Math.ceil(totalPending / PAGE_SIZE)}
                currentPage={pendingPage}
                onPageChange={setPendingPage}
              />
            </div>
          </>
        )}
      </div>

      <div>
        <SectionHeader title="Review History" count={totalReviewed} colorClass="secondary" />
        {reviewedItems.length === 0 ? (
          <Card className="shadow-sm" style={{ border: "1px solid #e9ecef", borderRadius: "0.5rem" }}>
            <CardBody className="text-center py-5 text-muted">
              No reviewed changes yet.
            </CardBody>
          </Card>
        ) : (
          <>
            {reviewedItems.map((change) => (
              <ChangeCard
                key={change._id}
                change={change}
                expanded={expandedId === change._id}
                onToggleExpand={onToggleExpand}
                onApprove={onApprove}
                onApproveChangeset={onApproveChangeset}
                onReject={onReject}
                isPending={false}
                allProperties={allProperties}
                currentSiteId={currentSiteId}
              />
            ))}
            <div className="d-flex justify-content-center mt-3">
              <GenericPagination
                totalPages={Math.ceil(totalReviewed / PAGE_SIZE)}
                currentPage={reviewedPage}
                onPageChange={setReviewedPage}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default DisplayPendingChangesView
